"use client";

/* eslint-disable no-unused-vars */
import React, {
  forwardRef,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import type { TributeOptions } from "tributejs";
import { Textarea } from "./textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "./dropdown-menu";

import { Button } from "./button";
import { Input } from "./input";
import { cn } from "@aichat/ui/lib/utils";
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu";
import BracketsWithXIcon from "../icons/BracketsWithXIcon";
import { BoldIcon, ItalicIcon } from "lucide-react";

export type VariableInput = {
  key: string;
  value: string;
  prefix?: string;
  group: string;
  disabled?: boolean;
};

export type VariableSelectorInputProps = {
  value: string;
  onChange: (value: string) => void;
  trigger?: string;
  items?: { prefix?: string; key: string; value: string; group: string }[];
  singleLine?: boolean;
  textAreaClassName?: string;
  textAreaRows?: number;
  maxLength?: number;
  inputClassName?: string;
  placeholder?: string;
};

type TributeType<T> = {
  isActive: boolean;
  append(index: number, values: Array<T>, replace?: boolean): void;
  appendCurrent(values: Array<T>, replace?: boolean): void;
  attach(to: Element): void;
  detach(to: Element): void;
  showMenuForCollection(input: Element, collectionIndex?: number): void;
};

export const constructVariableString = (value: VariableInput) => {
  return value.prefix ? `${value.prefix}.${value.value}` : value.value;
};

export const VariableSelectorInput = forwardRef<
  HTMLTextAreaElement,
  VariableSelectorInputProps
>(
  (
    {
      value,
      onChange,
      trigger = "{{",
      items = [],
      singleLine,
      textAreaClassName,
      textAreaRows,
      maxLength,
      inputClassName,
      placeholder,
    },
    forwardedRef,
  ) => {
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(forwardedRef, () => textAreaRef.current!, []);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const memoizedItems = useMemo(() => items, [items]);

    const groupedItems = useMemo(() => {
      if (!items || items.length === 0) return {};

      return items.reduce(
        (acc, item) => {
          if (!acc[item.group]) {
            acc[item.group] = [];
          }
          acc[item.group]?.push(item);
          return acc;
        },
        {} as Record<string, VariableInput[]>,
      );
    }, [items]);

    const mainContainerRef = React.useRef<HTMLDivElement>(null);

    const id = useId();
    const ref: React.RefObject<HTMLTextAreaElement | HTMLInputElement> =
      singleLine ? inputRef : textAreaRef;

    React.useEffect(() => {
      let currentInput: HTMLTextAreaElement | HTMLInputElement | null = null;
      let tribute: TributeType<{
        prefix?: string;
        key: string;
        value: string;
        group: string;
      }> | null = null;

      function preventOverflow() {
        // if value has unbalance {{ }} then set open to true else set to false
        const nodeAccordionContent = document.querySelectorAll(
          "[data-node-accordion-content]",
        );

        if (nodeAccordionContent) {
          if (tribute!.isActive) {
            nodeAccordionContent.forEach((item) =>
              item.classList.remove("overflow-hidden"),
            );
          } else {
            nodeAccordionContent.forEach((item) =>
              item.classList.add("overflow-hidden"),
            );
          }
        }
      }

      function handleInput() {
        if (currentInput) {
          onChange(currentInput.value);
          preventOverflow();
        }
      }

      async function runEffect() {
        const options: TributeOptions<{
          prefix?: string;
          key: string;
          value: string;
          group: string;
        }> = {
          collection: [
            {
              trigger: trigger,
              values: memoizedItems,
              menuShowMinLength: 0,
              selectTemplate: (item) =>
                `{{${constructVariableString(item.original as VariableInput)}}}`,
              containerClass:
                "bg-white min-w-[150px] rounded-lg z-[999999999] shadow-sm border border-input p-1 max-h-[250px] pr-1 overflow-auto",
              menuItemTemplate: (item) => {
                const originalItem = item.original as VariableInput;
                // Find current item in items list
                const itemIdx = memoizedItems.findIndex(
                  (i) =>
                    constructVariableString(i) ===
                    constructVariableString(originalItem),
                );

                const prevItem = memoizedItems[itemIdx - 1];
                const currentItemGroup = originalItem.group;
                const nextItemGroup = prevItem?.group;

                let isGroupChanged = false;
                if (currentItemGroup !== nextItemGroup) isGroupChanged = true;
                return `
                    ${isGroupChanged ? `<div class="text-sm font-semibold px-2 py-1.5">${originalItem.group}</div>` : ""}
                    <div data-variable-input class="flex select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-background-100 focus:bg-background-100 cursor-pointer">
                      ${originalItem.value}
                    </div>
                  `;
              },
            },
          ],

          menuContainer: mainContainerRef.current as HTMLDivElement,
          positionMenu: true,
        };

        const Tribute = (await import("tributejs")).default as any;

        tribute = new Tribute(options) as TributeType<{
          prefix?: string;
          key: string;
          value: string;
          group: string;
        }>;

        currentInput = ref.current;

        if (currentInput) {
          tribute.attach(currentInput);
          currentInput.addEventListener("input", handleInput);
        }
      }

      runEffect();

      return () => {
        if (!currentInput) return;
        tribute?.detach(currentInput);
        currentInput.removeEventListener("input", handleInput);
      };
    }, [onChange, trigger, memoizedItems]);

    return (
      <div ref={mainContainerRef} className="relative">
        {singleLine ? (
          <Input
            className={inputClassName}
            ref={inputRef}
            value={value}
            autoComplete="off"
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                const nodeAccordionContent = document.querySelectorAll(
                  "[data-node-accordion-content]",
                );

                nodeAccordionContent.forEach((item) =>
                  item.classList.add("overflow-hidden"),
                );
              }
            }}
            id={id}
            placeholder={placeholder}
          />
        ) : (
          <Textarea
            className={textAreaClassName}
            rows={textAreaRows}
            maxLength={maxLength}
            value={value}
            autoComplete="off"
            placeholder={placeholder}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                const nodeAccordionContent = document.querySelectorAll(
                  "[data-node-accordion-content]",
                );

                nodeAccordionContent.forEach((item) =>
                  item.classList.add("overflow-hidden"),
                );
              }
            }}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            id={id}
            ref={textAreaRef}
          />
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className={cn(
                "w-8 h-7 absolute px-0 py-0 border-secondary bg-purple-50 hover:bg-purple-100",
                singleLine
                  ? "top-1/2 right-[5px] -translate-y-1/2"
                  : "bottom-[10px] right-[10px]",
              )}
            >
              <BracketsWithXIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="max-h-[250px] overflow-auto">
            {items.length > 0 ? (
              Object.entries(groupedItems).map(([group, items], groupIdx) => (
                <DropdownMenuGroup key={group}>
                  {groupIdx > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuLabel>{group}</DropdownMenuLabel>
                  {items.map((item) => (
                    <DropdownMenuItem
                      key={item.key}
                      onClick={() => {
                        onChange(
                          `${value}${value.length > 0 ? " " : ""}{{${constructVariableString(item)}}}`,
                        );
                      }}
                    >
                      {item.value}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              ))
            ) : (
              <DropdownMenuItem disabled>No items available</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  },
);

type FormatType = "bold" | "italic";

const getWrapper = (type: FormatType) => {
  switch (type) {
    case "bold":
      return "*";
    case "italic":
      return "_";
  }
};

const wrapSelectedOrLastWord = ({
  text,
  selectionStart,
  selectionEnd,
  wrapper,
  mode,
}: {
  text: string;
  selectionStart: number;
  selectionEnd: number;
  wrapper: string;
  mode: "select";
}) => {
  if (mode === "select" && selectionStart !== selectionEnd) {
    const selectedText = text.slice(selectionStart, selectionEnd);
    const newText =
      text.slice(0, selectionStart) +
      wrapper +
      selectedText +
      wrapper +
      text.slice(selectionEnd);
    return {
      newText,
      newStart: selectionStart + wrapper.length,
      newEnd: selectionEnd + wrapper.length,
    };
  }
  return null;
};

type VariableSelectorInputWithToolbarProps = VariableSelectorInputProps;

export const VariableSelectorInputWithToolbar = (
  variableSelectorInputProps: VariableSelectorInputWithToolbarProps,
) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const handleFormatClick = (type: FormatType) => {
    const textarea = textAreaRef.current;
    if (!textarea) return;

    const wrapper = getWrapper(type);

    const result = wrapSelectedOrLastWord({
      text: textarea.value,
      selectionStart: textarea.selectionStart,
      selectionEnd: textarea.selectionEnd,
      wrapper,
      mode: "select",
    });

    if (result) {
      variableSelectorInputProps.onChange(result.newText);
      setTimeout(() => {
        textarea.setSelectionRange(result.newStart!, result.newEnd!);
      }, 0);
    }

    textarea.focus();
  };

  return (
    <div>
      <div className="flex gap-2 py-2 px-1 border border-gray-200 rounded-tl-md rounded-tr-md relative top-1 pb-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleFormatClick("bold")}
        >
          <BoldIcon className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleFormatClick("italic")}
        >
          <ItalicIcon className="size-4" />
        </Button>
      </div>
      <VariableSelectorInput
        ref={textAreaRef}
        {...variableSelectorInputProps}
        onChange={variableSelectorInputProps.onChange}
      />
    </div>
  );
};
